/**
 * Turn a FastAPI/HTTPException response into a user-readable string.
 *
 * FastAPI's `detail` can be:
 *   - a string  → return as-is
 *   - an object → return `.msg` or JSON stringify
 *   - an array (Pydantic validation) → join `.msg` fields
 *   - undefined → fallback
 *
 * Without this, `toast.error(data.detail)` renders "[object Object]" when the
 * backend returns a Pydantic validation error, which is what users saw on
 * /admin/residents when invites failed.
 */
export function extractErrorMessage(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  // Native Error instance
  if (err instanceof Error) return err.message || fallback;

  // FastAPI style: { detail: … }
  const detail = err.detail !== undefined ? err.detail : err;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((it) => (typeof it === 'string' ? it : it?.msg || it?.detail || null))
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }

  if (detail && typeof detail === 'object') {
    if (typeof detail.msg === 'string') return detail.msg;
    if (typeof detail.message === 'string') return detail.message;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }

  return fallback;
}
